
// planet-aspects.ui.js
// UI "Aspetti": bottone in toolbar + finestra con
// - selezione coppie pianeti
// - checklist aspetti (☌, ✶, □, △, ☍)
// - orbita (default 3°)
// - retrogradazioni per pianeta
// - Evidenzia durata (banda START–END)
// - Analisi statistica (parametri) + tabella outpu
// - Applica -> chiama il controller e disegna

import { PLANET_KEYS } from './planet-aspects.js';
import { computeAspectStats, renderStatsTable } from './planet-aspects-stats.js';


const PLANET_LABEL = {
  SOLE:'Sole', LUNA:'Luna', MERCURIO:'Mercurio', VENERE:'Venere', MARTE:'Marte',
  GIOVE:'Giove', SATURNO:'Saturno', URANO:'Urano', NETTUNO:'Nettuno'
};
const PLANET_ICON = { SOLE:'☉', LUNA:'☽', MERCURIO:'☿', VENERE:'♀', MARTE:'♂', GIOVE:'♃', SATURNO:'♄', URANO:'♅', NETTUNO:'♆' };

const ASPECT_ITEMS = [
  { key:'CONJ', label:'Congiunzione',  icon:'☌' },
  { key:'SEXT', label:'Sestile',       icon:'✶' },
  { key:'SQUARE', label:'Quadratura',  icon:'□' },
  { key:'TRINE', label:'Trigono',      icon:'△' },
  { key:'OPP', label:'Opposizione',    icon:'☍' }
];

// CSS minimal per la finestra
function injectAspectsCss() {
    const css = `
    .asp-modal-backdrop{ position:fixed; inset:0; background:rgba(0,0,0,.35);
        display:flex; align-items:center; justify-content:center; z-index:99998; }
    .asp-modal{ background:#fff; color:#222; width:min(880px,94vw); max-height:88vh; overflow:auto;
        border:1px solid #E0E0E0; border-radius:10px; box-shadow:0 18px 48px rgba(0,0,0,.20);
        font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; }
    .asp-hd{ display:flex; align-items:center; justify-content:space-between; padding:14px 16px; border-bottom:1px solid #ECECEC; }
    .asp-ttl{ font-size:16px; font-weight:700; }
    .asp-cls{ background:transparent; border:none; font-size:20px; line-height:20px; cursor:pointer; color:#666; }
    .asp-bd{ padding:12px 16px; display:grid; gap:12px; }
    .asp-row{ display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
    .asp-col{ display:flex; flex-direction:column; gap:8px; }
    .asp-field{ display:flex; align-items:center; gap:8px; }
    .asp-select{ padding:6px 8px; border:1px solid #E0E0E0; border-radius:6px; background:#fff; }
    .asp-chip{ display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border:1px solid #E0E0E0; border-radius:999px; background:#F7F9FC; }
    .asp-chip .rm{ cursor:pointer; color:#A00; font-weight:700; margin-left:4px; }
    .asp-grid{ display:grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap:10px; }
    .asp-check{ display:flex; gap:8px; align-items:center; }
    .asp-actions{ display:flex; gap:8px; padding:8px 16px 16px 16px; border-top:1px solid #ECECEC; justify-content:flex-end; }
    .asp-btn{ padding:8px 12px; border-radius:6px; border:1px solid #E0E0E0; background:#F5F7FA; color:#222; cursor:pointer; }
    .asp-btn:hover{ background:#EEF2F7; }
    .asp-primary{ background:#1976D2; border-color:#1976D2; color:#fff; }
    .asp-primary:hover{ background:#165fa8; }
    .asp-h2{ font-weight:700; margin-top:12px; }
    /* tabella stats */
    .asp-table{ width:100%; border-collapse:collapse; margin-top:8px; }
    .asp-table th, .asp-table td{ border:1px solid #E0E0E0; padding:6px 8px; text-align:center; }
    .asp-table th{ background:#F7F9FC; }
    `;
  const st = document.createElement('style');
  st.id = 'asp-ui-css';
  st.textContent = css;
  if (!document.getElementById('asp-ui-css')) document.head.appendChild(st);
  return st;
}

export function initAspectsUI(widget, aspectsCtrl, options = {}) {
  const {
    aspectsButtonTitle = 'Aspetti',
    tooltipAspects = 'Configura e visualizza gli aspetti planetari',
    defaultPairs = [],                 // coppie iniziali (vuoto)
    defaultAspects = [],               // nessuno check di default
    defaultOrb = 3,                    // orbita default 3°
    defaultRetroPlanets = [],           // retrogradazioni (vuoto)
    defaultHighlight = false
  } = options;

  const state = {
    btnAspects: null,
    backdrop: null,
    modal: null,
    cssEl: null,
    disposed: false,

    pairs: [...defaultPairs],          // array di coppie [['VENERE','MARTE'], ...]
    aspects: new Set(defaultAspects),  // aspetti selezionati
    orb: defaultOrb,
    retro: new Set(defaultRetroPlanets),
    highlightDuration: !!defaultHighlight
  };

  state.cssEl = injectAspectsCss();

  widget.headerReady().then(() => {
    const btn = widget.createButton();
    btn.textContent = aspectsButtonTitle;
    btn.setAttribute('title', tooltipAspects);
    state.btnAspects = btn;
    btn.addEventListener('click', () => openModal());
  });

  // Helpers
  const openModal = () => {
    if (state.disposed || state.backdrop) return;

    // Backdrop & Modal
    const backdrop = document.createElement('div'); backdrop.className = 'asp-modal-backdrop';
    const modal = document.createElement('div'); modal.className = 'asp-modal'; 
    state.backdrop = backdrop; state.modal = modal;

    // Header
    const hd = document.createElement('div'); hd.className = 'asp-hd';
    const ttl = document.createElement('div'); ttl.className = 'asp-ttl'; ttl.textContent = 'Aspetti planetari';
    const cls = document.createElement('button'); cls.className = 'asp-cls'; cls.innerHTML = '×';
    hd.appendChild(ttl); hd.appendChild(cls);

    // Body
    const bd = document.createElement('div'); bd.className = 'asp-bd';

    // Selettori coppie
    const rowPairs = document.createElement('div'); rowPairs.className = 'asp-row';
    const selA = document.createElement('select'); selA.className = 'asp-select';
    const selB = document.createElement('select'); selB.className = 'asp-select';

    const optPlaceholder = (txt) => { const o = document.createElement('option'); o.value = ''; o.textContent = txt; return o; };
    selA.appendChild(optPlaceholder('Pianeta A'));
    selB.appendChild(optPlaceholder('Pianeta B'));
    for (const k of PLANET_KEYS) {
      const o1 = document.createElement('option'); o1.value = k; o1.textContent = `${PLANET_ICON[k]} ${PLANET_LABEL[k]}`;
      const o2 = document.createElement('option'); o2.value = k; o2.textContent = `${PLANET_ICON[k]} ${PLANET_LABEL[k]}`;
      selA.appendChild(o1); selB.appendChild(o2);
    }
    const btnAddPair = document.createElement('button'); btnAddPair.className = 'asp-btn'; btnAddPair.textContent = 'Aggiungi coppia';
    const pairsWrap = document.createElement('div'); pairsWrap.className = 'asp-row'; // chips container

    const renderPairs = () => {
      pairsWrap.innerHTML = '';
      state.pairs.forEach(([a,b], idx) => {
        const chip = document.createElement('span'); chip.className = 'asp-chip';
        chip.textContent = `${PLANET_ICON[a]} ${PLANET_LABEL[a]}  –  ${PLANET_ICON[b]} ${PLANET_LABEL[b]}`;
        const rm = document.createElement('span'); rm.className = 'rm'; rm.textContent = '×';
        rm.addEventListener('click', () => { state.pairs.splice(idx,1); renderPairs(); });
        chip.appendChild(rm);
        pairsWrap.appendChild(chip);
      });
    };
    renderPairs();

    btnAddPair.addEventListener('click', () => {
      const a = selA.value, b = selB.value;
      if (!a || !b || a === b) return;
      // normalizza coppia senza duplicati A-B == B-A
      const keyA = a < b ? a : b, keyB = a < b ? b : a;
      const exists = state.pairs.some(([x,y]) => (x===keyA && y===keyB));
      if (!exists) {
        state.pairs.push([keyA, keyB]);
        renderPairs();
      }
    });

    rowPairs.appendChild(selA); rowPairs.appendChild(selB); rowPairs.appendChild(btnAddPair);

    // Selettori aspetti
    const aspCol = document.createElement('div'); aspCol.className = 'asp-col';
    const aspTitle = document.createElement('div'); aspTitle.textContent = 'Tipi di aspetto';
    const aspGrid = document.createElement('div'); aspGrid.className = 'asp-grid';
    ASPECT_ITEMS.forEach(({key,label,icon}) => {
      const wrap = document.createElement('label'); wrap.className = 'asp-check';
      const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = key;
      cb.checked = state.aspects.has(key);
      cb.addEventListener('change', () => { cb.checked ? state.aspects.add(key) : state.aspects.delete(key); });
      const tx = document.createElement('span'); tx.textContent = `${icon} ${label}`;
      wrap.appendChild(cb); wrap.appendChild(tx); aspGrid.appendChild(wrap);
    });
    aspCol.appendChild(aspTitle); aspCol.appendChild(aspGrid);

    // Orbita
    const orbRow = document.createElement('div'); orbRow.className = 'asp-field';
    const orbLbl = document.createElement('label'); orbLbl.textContent = 'Orbita (tolleranza in gradi):';
    const orbInp = document.createElement('input'); orbInp.type = 'number'; orbInp.min = '0'; orbInp.max = '10'; orbInp.step = '1'; orbInp.value = String(state.orb ?? 3);
    orbInp.className = 'asp-select';
    orbInp.addEventListener('input', () => {
      const v = parseFloat(orbInp.value); state.orb = (Number.isFinite(v) ? v : 3);
    });
    orbRow.appendChild(orbLbl); orbRow.appendChild(orbInp);

    // Retrogradazioni (lista pianeti)
    const retroCol = document.createElement('div'); retroCol.className = 'asp-col';
    const retroTitle = document.createElement('div'); retroTitle.textContent = 'Retrogradazioni (pianeti)';
    const retroGrid = document.createElement('div'); retroGrid.className = 'asp-grid';
    PLANET_KEYS.forEach(k => {
      const wrap = document.createElement('label'); wrap.className = 'asp-check';
      const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = k;
      cb.checked = state.retro.has(k);
      cb.addEventListener('change', () => { cb.checked ? state.retro.add(k) : state.retro.delete(k); });
      const tx = document.createElement('span'); tx.textContent = `${PLANET_ICON[k]} ${PLANET_LABEL[k]}`;
      wrap.appendChild(cb); wrap.appendChild(tx); retroGrid.appendChild(wrap);
    });
    retroCol.appendChild(retroTitle); retroCol.appendChild(retroGrid);


    // Evidenzia durata
    const hlRow = document.createElement('div'); hlRow.className = 'asp-check';
    const hlCb = document.createElement('input'); hlCb.type = 'checkbox'; hlCb.checked = false;
    const hlLbl = document.createElement('span'); hlLbl.textContent = 'Evidenzia durata (banda semitrasparente)';
    hlCb.addEventListener('change', () => { state.highlightDuration = hlCb.checked; });
    hlRow.appendChild(hlCb); hlRow.appendChild(hlLbl);
    bd.appendChild(hlRow);


    // --- Sezione Analisi Statistica ---
    const statsTitle = document.createElement('div'); statsTitle.className = 'asp-h2'; statsTitle.textContent = 'Analisi statistica';
    const winRow = document.createElement('div'); winRow.className = 'asp-field';
    const winLbl = document.createElement('label'); winLbl.textContent = 'Finestra ± barre:';
    const winInp = document.createElement('input'); winInp.type = 'number'; winInp.min = '1'; winInp.value = '10'; winInp.className = 'asp-select';
    winRow.appendChild(winLbl); winRow.appendChild(winInp);

    const barsRow = document.createElement('div'); barsRow.className = 'asp-field';
    const barsLbl = document.createElement('label'); barsLbl.textContent = 'Barre per accelerazione:';
    const barsInp = document.createElement('input'); barsInp.type = 'number'; barsInp.min = '2'; barsInp.value = '5'; barsInp.className = 'asp-select';
    barsRow.appendChild(barsLbl); barsRow.appendChild(barsInp);

    const accelRow = document.createElement('div'); accelRow.className = 'asp-field';
    const accelLbl = document.createElement('label'); accelLbl.textContent = 'Soglia accelerazione (%):';
    const accelInp = document.createElement('input'); accelInp.type = 'number'; accelInp.min = '0.5'; accelInp.step = '0.1'; accelInp.value = '3'; accelInp.className = 'asp-select';
    accelRow.appendChild(accelLbl); accelRow.appendChild(accelInp);

    const btnStats = document.createElement('button'); btnStats.className = 'asp-btn'; btnStats.textContent = 'Calcola statistica';
    const statsContainer = document.createElement('div');

    // Compose body
    bd.appendChild(rowPairs);
    bd.appendChild(pairsWrap);
    bd.appendChild(aspCol);
    bd.appendChild(orbRow);
    bd.appendChild(retroCol);
    bd.appendChild(statsTitle);
    bd.appendChild(winRow);
    bd.appendChild(accelRow);
    bd.appendChild(btnStats);
    bd.appendChild(statsContainer);

    // Footer actions
    const ft = document.createElement('div'); ft.className = 'asp-actions';
    const btnClose = document.createElement('button'); btnClose.className = 'asp-btn'; btnClose.textContent = 'Chiudi';
    const btnApply = document.createElement('button'); btnApply.className = 'asp-btn asp-primary'; btnApply.textContent = 'Applica';

    ft.appendChild(btnClose); ft.appendChild(btnApply);

    // Mount
    modal.appendChild(hd); modal.appendChild(bd); modal.appendChild(ft);
    backdrop.appendChild(modal); document.body.appendChild(backdrop);

    // Handlers base
    const close = () => {
      if (!state.backdrop) return;
      document.removeEventListener('keydown', onEsc, true);
      try { state.backdrop.remove(); } catch {}
      state.backdrop = null; state.modal = null;
    };
    const onEsc = (ev) => { if (ev.key === 'Escape') close(); };

    cls.addEventListener('click', close);
    btnClose.addEventListener('click', close);
    document.addEventListener('keydown', onEsc, true);
    backdrop.addEventListener('click', (ev) => { if (ev.target === backdrop) close(); });

    // Applica → manda impostazioni al controller e mostra, accende se OFF
    btnApply.addEventListener('click', async () => {
      // pairs / aspetti / orb / retro
      await aspectsCtrl.setPairs(state.pairs);
      await aspectsCtrl.setAspects([...state.aspects]);
      await aspectsCtrl.setOrb(state.orb);
      await aspectsCtrl.setRetroPlanets([...state.retro]);
      await aspectsCtrl.setHighlightDuration(state.highlightDuration);


        // applica il toggle della banda
        if (typeof aspectsCtrl.setHighlightDuration === 'function') {
            await aspectsCtrl.setHighlightDuration(!!state.highlightDuration);
        }

        // accendi se era spento
        if (!aspectsCtrl.isActive || !aspectsCtrl.isActive()) {
            await aspectsCtrl.show();
        }
        close();

    });

    // Calcolo statistica (intero dataset)
    btnStats.addEventListener('click', async () => {
      statsContainer.textContent = 'Calcolo in corso...';
      try {
        const windowSize = Math.max(1, parseInt(winInp.value, 10) || 10);
        const accelBars = Math.max(2, parseInt(barsInp.value, 10) || 5);
        const accelThreshold = (parseFloat(accelInp.value) || 3) / 100;


        // Assicura che il controller abbia le scelte correnti del modal
        await aspectsCtrl.setPairs(state.pairs);
        await aspectsCtrl.setAspects([...state.aspects]);
        await aspectsCtrl.setOrb(state.orb);
        await aspectsCtrl.setRetroPlanets([...state.retro]);


        const intervals = await aspectsCtrl.getAllIntervals();
        const ohlc = await aspectsCtrl.getOhlcAll();

        if (!Array.isArray(intervals) || !intervals.length) {
          statsContainer.textContent = 'Nessun evento disponibile per la statistica.';
          return;
        }
        if (!Array.isArray(ohlc) || !ohlc.length) {
          statsContainer.textContent = 'Dati OHLC non disponibili. Abilita exportData o fornisci OHLC via injection.';
          return;
        }

        const stats = computeAspectStats(intervals, ohlc, { windowSize, accelBars, accelThreshold });
        const table = renderStatsTable(stats, { className: 'asp-table' });
        statsContainer.innerHTML = '';
        statsContainer.appendChild(table);
      } catch (e) {
        console.error(e);
        statsContainer.textContent = 'Errore durante il calcolo della statistica.';
      }
    });

  };

  function dispose() {
    if (state.disposed) return;
    state.disposed = true;
    try { state.btnAspects?.remove(); } catch {}
    try { state.backdrop?.remove(); } catch {}
    try { state.cssEl?.remove(); } catch {}
  }

  return { dispose };
}

/* ===========================
   Come integrare (promemoria)
   ===========================
import { initPlanetAspects } from './planet-aspects.js';
import { initAspectsUI } from './planet-aspects.ui.js';

widget.onChartReady(async () => {
  const aspectsCtrl = await initPlanetAspects(widget, {
    csvUrl: '/assets/effemeridi_sessadecimali_2010_2050.csv',
    selectedPairs: [],
    selectedAspects: [],
    orbDeg: 3,
    retroPlanets: []
  });

  const aspectsUI = initAspectsUI(widget, aspectsCtrl, {
    aspectsButtonTitle: 'Aspetti',
    defaultPairs: [],
    defaultAspects: [],
    defaultOrb: 3,
    defaultRetroPlanets: []
  });

  // quando smonti:
  // aspectsUI.dispose();
  // aspectsCtrl.dispose();
});
*/
